import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Tooltip,
  Cell,
  type PieLabelRenderProps,
} from "recharts";

export interface CategoryDataPoint extends Record<string, unknown> {
  name: string;
  value: number;
  color: string;
}

interface CategoryDistributionPieProps {
  data: CategoryDataPoint[];
  fallbackLabel: string;
}

const RADIAN = Math.PI / 180;
const MIN_LABEL_PERCENT = 0.03;

// Outer label renderer (uses currentColor so it follows theme from wrapper).
const renderOuterLabel = (props: PieLabelRenderProps) => {
  const {
    cx = 0,
    cy = 0,
    midAngle = 0,
    outerRadius = 0,
    percent,
    payload,
  } = props;

  const pct = typeof percent === "number" ? percent : 0;
  if (pct <= 0 || Number.isNaN(pct) || pct < MIN_LABEL_PERCENT) return null;

  const safeCx = typeof cx === "number" ? cx : 0;
  const safeCy = typeof cy === "number" ? cy : 0;
  const safeOuter = typeof outerRadius === "number" ? outerRadius : 0;
  const safeMid = typeof midAngle === "number" ? midAngle : 0;

  const safePayload = (payload ?? {}) as Record<string, unknown>;
  const name = typeof safePayload.name === "string" ? safePayload.name : "";

  const angle = -safeMid * RADIAN;

  // Keep a moderate offset so labels don't get clipped.
  const offset = 18;
  const r = safeOuter + offset;

  const x = safeCx + r * Math.cos(angle);
  const y = safeCy + r * Math.sin(angle);

  const textAnchor = x >= safeCx ? "start" : "end";

  const labelText = name
    ? `${name} ${(pct * 100).toFixed(0)}%`
    : `${(pct * 100).toFixed(0)}%`;

  return (
    <text
      x={x}
      y={y}
      fill="currentColor"
      textAnchor={textAnchor}
      dominantBaseline="central"
      style={{ fontFamily: "inherit" }}
      fontSize={13}
      fontWeight={500}
    >
      {labelText}
    </text>
  );
};

export function CategoryDistributionPie({
  data,
  fallbackLabel,
}: CategoryDistributionPieProps) {
  const safeData: CategoryDataPoint[] = Array.isArray(data) ? data : [];
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;

  // Animation settings (clockwise sweep). Adjust to sync with other charts:
  // 650–900ms usually feels "in sync" with bar/line animations.
  const ANIM_MS = 300;

  // Respect reduced motion; also control label timing.
  const reduceMotion = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  // Hide labels while the pie is animating so text does NOT "move/rotate".
  const [showLabels, setShowLabels] = useState<boolean>(reduceMotion);

  useEffect(() => {
    if (reduceMotion) {
      setShowLabels(true);
      return;
    }

    setShowLabels(false);
    // Show labels slightly before the end so it feels more "in sync".
    const id = window.setTimeout(
      () => setShowLabels(true),
      Math.max(0, ANIM_MS - 120),
    );
    return () => window.clearTimeout(id);
  }, [reduceMotion]);

  return (
    // Match your subtitle / Top10 bar text color in both themes.
    <div className="w-full" style={primaryTextStyle}>
      <ResponsiveContainer width="100%" height={380}>
        <PieChart margin={{ top: 18, right: 90, bottom: 18, left: 90 }}>
          <Pie
            data={safeData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={125}
            innerRadius={60}
            // Clockwise sweep: start at top (12 o'clock) and go full circle.
            startAngle={90}
            endAngle={-270}
            // Show labels only after animation ends (prevents moving text).
            label={showLabels ? renderOuterLabel : false}
            labelLine={{
              stroke: "currentColor",
              strokeWidth: 1,
              opacity: 0.6,
            }}
            // Enable built-in animation (creates the sweep).
            isAnimationActive={!reduceMotion}
            animationBegin={0}
            animationDuration={ANIM_MS}
            animationEasing="ease-out"
          >
            {safeData.map((entry) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Pie>

          <Tooltip
            cursor={{ fill: "var(--muted)" }}
            content={({ active, payload }) => {
              if (!active || !payload || payload.length === 0) return null;

              const p = payload[0]?.payload as Partial<CategoryDataPoint>;
              const label =
                typeof p?.name === "string" && p.name.length > 0
                  ? p.name
                  : fallbackLabel;

              const rawValue = payload[0]?.value;
              const v =
                typeof rawValue === "number" ? rawValue : Number(rawValue);
              const displayValue = Number.isFinite(v)
                ? v.toLocaleString("de-DE")
                : String(rawValue);

              return (
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-900">
                  <div className="font-medium" style={primaryTextStyle}>
                    {label}
                  </div>
                  <div style={secondaryTextStyle}>{displayValue}</div>
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
